import { gql, useQuery } from "@apollo/client";
import { WBTypography } from "@apptractive/design-system-web";
import { userDateFromUnixSeconds } from "@apptractive/stm-shared";
import { Box } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { StripeSession } from "../../API";
import { LinkButton } from "../../components/common/LinkButton";
import { PageContainer } from "../../components/PageContainer";
import { CSGetSub as GET_SUB } from "../../customGraphql/queries";
import {
  getUser,
  stripeGetCheckoutSession as GET_CHECKOUT,
} from "../../graphql/queries";
import { getOnboardingPath } from "../../helpers/onboarding";
import { StripeCheckoutSubscription } from "../StripeCheckoutConfirm/StripeCheckoutSubscription";

const OnboardingStripeCheckoutConfirm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { data: subData } = useQuery(gql(GET_SUB));
  const { data: userData } = useQuery(gql(getUser), {
    variables: {
      id: subData?.sub,
    },
    skip: !subData?.sub,
  });

  const { data: checkoutData, loading: checkoutLoading } = useQuery(
    gql(GET_CHECKOUT),
    {
      variables: {
        sessionId: searchParams.get("sessionId"),
      },
      skip: !searchParams.get("sessionId"),
    }
  );

  const user = useMemo(() => userData?.getUser || {}, [userData]);

  const checkout: StripeSession = useMemo(
    () => checkoutData?.stripeGetCheckoutSession || {},
    [checkoutData]
  );

  return (
    <PageContainer>
      {searchParams.get("success") && (
        <>
          <WBTypography variant="h1">
            {t("orderPlaced", { ns: "purchase" })}
          </WBTypography>
          <WBTypography fontWeight="bold">
            {t("helloTitle", { ns: "common" })} {userData?.getUser?.firstName},
          </WBTypography>
          {!checkoutLoading && !checkout?.subscription?.trial_end && (
            <WBTypography mt={1}>
              {t("orderPlacedConfirmationEmail", { ns: "purchase" })}
            </WBTypography>
          )}

          {!checkoutLoading && checkout?.subscription?.trial_end && (
            <WBTypography mt={1}>
              {t("trialEndsOn", { ns: "purchase" })}:{" "}
              <Box component="span" fontWeight="bold">
                {userDateFromUnixSeconds(checkout.subscription.trial_end)}
              </Box>
            </WBTypography>
          )}

          {!checkoutLoading && checkout?.subscription?.trial_end && (
            <WBTypography mt={1}>
              {t("trialEndsPaymentUnlessCancel", { ns: "purchase" })}
            </WBTypography>
          )}

          <StripeCheckoutSubscription
            checkout={checkout}
            loading={checkoutLoading}
          />

          <LinkButton
            to={getOnboardingPath(user)}
            variant="contained"
            sx={{
              mt: 3,
              alignSelf: "center",
            }}
          >
            {t("continueTitle", { ns: "common" })}
          </LinkButton>
        </>
      )}

      {searchParams.get("cancelled") && (
        <>
          <WBTypography variant="h1">
            {t("orderCancelledTitle", { ns: "purchase" })}
          </WBTypography>
          <WBTypography>
            {t("orderCancelledDescription", { ns: "purchase" })}
          </WBTypography>
        </>
      )}
    </PageContainer>
  );
};

export default OnboardingStripeCheckoutConfirm;
